import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Divider,
    Box,
    Chip,
    IconButton,
    useTheme,
    alpha,
    Paper,
    Avatar,
    Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    HourglassEmpty as PendingIcon,
    Person as PersonIcon,
    Dns as InfoIcon,
    Business as BusinessIcon
} from '@mui/icons-material';

const ElicalLeadDetailsModal = ({ open, onClose, leadData }) => {
    const theme = useTheme();

    if (!leadData) {
        return null;
    }

    // Check if it's an advisor lead (source is only present in advisor leads)
    const isAdvisorLead = leadData.hasOwnProperty('source');
    // Check if it's an elical lead (lead_type is 'elical')
    const isElicalLead = leadData.lead_type === 'elical';

    const getStatusColor = (status) => {
        if (!status) return theme.palette.grey[500];
        
        switch (status?.toLowerCase()) {
            case 'eligible':
                return theme.palette.success.main;
            case 'not eligible':
                return theme.palette.error.main;
            case 'pending':
                return theme.palette.warning.main;
            case 'new':
                return theme.palette.info.main;
            case 'contacted':
                return theme.palette.secondary.main;
            case 'qualified':
                return theme.palette.success.main;
            default:
                return theme.palette.primary.main;
        }
    };

    const getStatusIcon = (status) => {
        if (!status) return null;
        
        switch (status?.toLowerCase()) {
            case 'eligible':
            case 'qualified':
                return <CheckCircleIcon fontSize="small" />;
            case 'not eligible':
                return <CancelIcon fontSize="small" />;
            case 'pending':
            case 'new':
            case 'contacted':
                return <PendingIcon fontSize="small" />;
            default:
                return null;
        }
    };

    const renderDetailItem = (label, value, icon = null) => (
        <Grid item xs={12} sm={6} md={4}>
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    height: '100%',
                    borderRadius: '16px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    transition: 'all 0.3s ease',
                    background: alpha(theme.palette.background.paper, 0.7),
                    backdropFilter: 'blur(8px)',
                    '&:hover': {
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                        transform: 'translateY(-2px)',
                        borderColor: alpha(theme.palette.primary.main, 0.2)
                    }
                }}
            >
                <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {icon && (
                            <Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main
                                }}
                            >
                                {icon}
                            </Avatar>
                        )}
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                fontSize: '0.75rem',
                                letterSpacing: '0.05em'
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>
                    <Typography 
                        variant="body1" 
                        fontWeight={500}
                        sx={{ 
                            ml: icon ? 4.5 : 0, 
                            wordBreak: 'break-word',
                            fontSize: '0.935rem'
                        }}
                    >
                        {value || 'N/A'}
                    </Typography>
                </Stack>
            </Paper>
        </Grid>
    );

    // Group data into logical categories for better organization
    const groupData = () => {
        const groups = {
            contactInfo: [
                { label: 'Email', value: leadData.email, icon: <EmailIcon fontSize="small" /> },
                { label: 'Phone', value: leadData.phone || leadData.mobile_number || leadData.mobileNumber, icon: <PhoneIcon fontSize="small" /> },
            ],
            advisorDetails: [
                { label: 'Date Created', value: leadData.date || leadData.create_datetime || leadData.createDatetime, icon: <CalendarIcon fontSize="small" /> },
                { label: 'Source', value: leadData.source, icon: <InfoIcon fontSize="small" /> }
            ],
            elicalDetails: [
                { label: 'Date Created', value: leadData.date || leadData.create_datetime || leadData.createDatetime, icon: <CalendarIcon fontSize="small" /> },
                { label: 'Phone', value: leadData.phone || leadData.mobile_number || leadData.mobileNumber, icon: <PhoneIcon fontSize="small" /> },
                { label: 'Status', value: leadData.status || 'Pending', icon: getStatusIcon(leadData.status || 'Pending') }
            ],
            eligibilityDetails: [
                { label: 'Eligibility Score', value: leadData.score ? `${leadData.score}%` : 'Not calculated', icon: <InfoIcon fontSize="small" /> },
                { label: 'Status', value: leadData.status, icon: getStatusIcon(leadData.status) },
                { label: 'Property Type', value: leadData.propertyType, icon: <BusinessIcon fontSize="small" /> },
                { label: 'Income', value: leadData.income, icon: <InfoIcon fontSize="small" /> },
                { label: 'Date Created', value: leadData.date || leadData.create_datetime || leadData.createDatetime, icon: <CalendarIcon fontSize="small" /> }
            ],
            addressInfo: [
                { label: 'Address', value: leadData.address, icon: <LocationIcon fontSize="small" /> },
                { label: 'City', value: leadData.city, icon: <LocationIcon fontSize="small" /> },
                { label: 'State', value: leadData.state, icon: <LocationIcon fontSize="small" /> },
                { label: 'Zip Code', value: leadData.zipCode, icon: <LocationIcon fontSize="small" /> },
                { label: 'Region', value: leadData.region, icon: <LocationIcon fontSize="small" /> }
            ],
            otherDetails: []
        };

        // Add any additional fields from the API that aren't explicitly handled
        Object.entries(leadData).forEach(([key, value]) => {
            // Skip fields we've already included
            const handledFields = [
                'id', 'name', 'email', 'phone', 'mobileNumber', 'mobile_number', 'status', 
                'date', 'createDatetime', 'create_datetime', 'score', 'propertyType', 'income',
                'address', 'city', 'state', 'zipCode', 'region', 'source',
                'lead_id', 'lead_uuid', 'lead_type', 'ip'
            ];
            
            if (!handledFields.includes(key) && 
                typeof value !== 'function' && 
                (typeof value !== 'object' || value === null || Array.isArray(value))) {
                
                // Format the key from camelCase to Title Case
                const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                
                // Format the value based on its type
                let formattedValue = value;
                if (Array.isArray(value)) {
                    formattedValue = value.join(', ');
                } else if (value === null) {
                    formattedValue = 'N/A';
                } else if (typeof value === 'boolean') {
                    formattedValue = value ? 'Yes' : 'No';
                } else {
                    formattedValue = String(value);
                }
                
                groups.otherDetails.push({ 
                    label: formattedKey, 
                    value: formattedValue, 
                    icon: <InfoIcon fontSize="small" /> 
                });
            }
        });

        return groups;
    };

    const groups = groupData();

    // Determine the modal title based on lead type
    const getModalTitle = () => {
        if (isAdvisorLead) return 'Advisor Lead Details';
        if (isElicalLead) return 'Eligibility Calculator Lead Details';
        return 'Lead Details';
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 12px 30px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                    backgroundImage: theme.palette.mode === 'light' 
                        ? 'linear-gradient(to bottom, #ffffff, #f8faff)' 
                        : 'linear-gradient(to bottom, #1a1f2e, #131723)',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '5px',
                        background: `linear-gradient(90deg, 
                            ${theme.palette.primary.dark} 0%, 
                            ${theme.palette.primary.main} 50%,
                            ${theme.palette.primary.dark} 100%)`
                    }
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 3,
                background: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                        sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.15), 
                            color: theme.palette.primary.main,
                            mr: 2,
                            width: 42,
                            height: 42
                        }}
                    >
                        <PersonIcon />
                    </Avatar>
                    <Typography 
                        variant="h6" 
                        component="div" 
                        fontWeight="bold"
                        sx={{ 
                            color: theme.palette.text.primary,
                            fontFamily: "'Poppins', sans-serif",
                            letterSpacing: '0.02em',
                            fontSize: '1.125rem'
                        }}
                    >
                        {getModalTitle()}
                    </Typography>
                </Box>
                <IconButton 
                    edge="end" 
                    onClick={onClose} 
                    aria-label="close"
                    sx={{ 
                        color: alpha(theme.palette.primary.main, 0.8),
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            transform: 'rotate(90deg)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent 
                sx={{ 
                    p: 4, 
                    background: alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: 'blur(10px)',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        borderRadius: '8px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.3)
                        }
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: alpha(theme.palette.divider, 0.1),
                        borderRadius: '8px'
                    }
                }}
            >
                <Box 
                    sx={{ 
                        mb: 4, 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        pb: 3,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '10%',
                            right: '10%',
                            height: '1px',
                            background: `linear-gradient(90deg, 
                                transparent 0%, 
                                ${alpha(theme.palette.primary.main, 0.3)} 50%, 
                                transparent 100%)`
                        }
                    }}
                >
                    <Box>
                        {!isAdvisorLead && !isElicalLead && (
                            <Typography 
                                variant="h5" 
                                fontWeight="bold"
                                sx={{ 
                                    fontSize: '1.35rem',
                                    mb: 1,
                                    background: `linear-gradient(90deg, 
                                        ${theme.palette.primary.dark} 0%, 
                                        ${theme.palette.primary.main} 60%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontFamily: "'Poppins', sans-serif"
                                }}
                            >
                                {leadData.name}
                            </Typography>
                        )}
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: alpha(theme.palette.text.secondary, 0.9),
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontFamily: "'Poppins', sans-serif"
                            }}
                        >
                            <Chip 
                                label={`ID: ${leadData.id || leadData.lead_id}`}
                                size="small"
                                sx={{ 
                                    fontWeight: 600,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                    borderRadius: '6px',
                                    px: 0.5
                                }}
                            />
                        </Typography>
                    </Box>
                    {leadData.status && (
                        <Chip
                            icon={getStatusIcon(leadData.status)}
                            label={leadData.status}
                            sx={{ 
                                bgcolor: `${alpha(getStatusColor(leadData.status), 0.1)}`,
                                color: getStatusColor(leadData.status),
                                fontWeight: 600,
                                px: 1,
                                borderRadius: '8px',
                                boxShadow: `0 2px 8px ${alpha(getStatusColor(leadData.status), 0.15)}`,
                                border: `1px solid ${alpha(getStatusColor(leadData.status), 0.3)}`,
                                height: 32,
                                '& .MuiChip-icon': {
                                    color: getStatusColor(leadData.status)
                                }
                            }}
                        />
                    )}
                </Box>

                {/* Phone Information for Advisor or Elical leads */}
                {(isAdvisorLead || isElicalLead) && (
                    <Box sx={{ mb: 4 }}>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            gutterBottom
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 2.5,
                                fontFamily: "'Poppins', sans-serif",
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -8,
                                    left: 0,
                                    width: 50,
                                    height: 3,
                                    background: theme.palette.primary.main,
                                    borderRadius: '8px'
                                }
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main
                                }}
                            >
                                <PhoneIcon fontSize="small" />
                            </Avatar>
                            Contact Information
                        </Typography>
                        <Grid container spacing={3}>
                            <React.Fragment>
                                {renderDetailItem('Phone', leadData.phone || leadData.mobile_number || leadData.mobileNumber || 'N/A', <PhoneIcon fontSize="small" />)}
                            </React.Fragment>
                        </Grid>
                    </Box>
                )}

                {/* Contact Information for other lead types */}
                {!isAdvisorLead && !isElicalLead && (
                    <Box sx={{ mb: 4 }}>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            gutterBottom
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 2.5,
                                fontFamily: "'Poppins', sans-serif",
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -8,
                                    left: 0,
                                    width: 50,
                                    height: 3,
                                    background: theme.palette.primary.main,
                                    borderRadius: '8px'
                                }
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main
                                }}
                            >
                                <PersonIcon fontSize="small" />
                            </Avatar>
                            Contact Information
                        </Typography>
                        <Grid container spacing={3}>
                            {groups.contactInfo.map((item, index) => (
                                item.value && <React.Fragment key={`contact-${index}`}>
                                    {renderDetailItem(item.label, item.value, item.icon)}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Advisor Lead Details */}
                {isAdvisorLead && groups.advisorDetails.some(item => item.value) && (
                    <Box sx={{ mb: 4 }}>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            gutterBottom
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 2.5,
                                fontFamily: "'Poppins', sans-serif",
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -8,
                                    left: 0,
                                    width: 50,
                                    height: 3,
                                    background: theme.palette.primary.main,
                                    borderRadius: '8px'
                                }
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main
                                }}
                            >
                                <InfoIcon fontSize="small" />
                            </Avatar>
                            Advisor Lead Details
                        </Typography>
                        <Grid container spacing={3}>
                            {groups.advisorDetails.map((item, index) => (
                                item.value && <React.Fragment key={`advisor-${index}`}>
                                    {renderDetailItem(item.label, item.value, item.icon)}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Elical Lead Details */}
                {isElicalLead && (
                    <Box sx={{ mb: 4 }}>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            gutterBottom
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 2.5,
                                fontFamily: "'Poppins', sans-serif",
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -8,
                                    left: 0,
                                    width: 50,
                                    height: 3,
                                    background: theme.palette.primary.main,
                                    borderRadius: '8px'
                                }
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main
                                }}
                            >
                                <InfoIcon fontSize="small" />
                            </Avatar>
                            Lead Details
                        </Typography>
                        <Grid container spacing={3}>
                            {groups.elicalDetails.map((item, index) => (
                                item.value && item.label !== 'Phone' && <React.Fragment key={`elical-${index}`}>
                                    {renderDetailItem(item.label, item.value, item.icon)}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Eligibility Details for non-elical, non-advisor leads */}
                {!isAdvisorLead && !isElicalLead && (
                    <Box sx={{ mb: 4 }}>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            gutterBottom
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 2.5,
                                fontFamily: "'Poppins', sans-serif",
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -8,
                                    left: 0,
                                    width: 50,
                                    height: 3,
                                    background: theme.palette.primary.main,
                                    borderRadius: '8px'
                                }
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main
                                }}
                            >
                                <InfoIcon fontSize="small" />
                            </Avatar>
                            Eligibility Details
                        </Typography>
                        <Grid container spacing={3}>
                            {groups.eligibilityDetails.map((item, index) => (
                                item.value && <React.Fragment key={`eligibility-${index}`}>
                                    {renderDetailItem(item.label, item.value, item.icon)}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Address Information (if available) */}
                {groups.addressInfo.some(item => item.value) && (
                    <Box sx={{ mb: 4 }}>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            gutterBottom
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 2.5,
                                fontFamily: "'Poppins', sans-serif",
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -8,
                                    left: 0,
                                    width: 50,
                                    height: 3,
                                    background: theme.palette.primary.main,
                                    borderRadius: '8px'
                                }
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main
                                }}
                            >
                                <LocationIcon fontSize="small" />
                            </Avatar>
                            Address Information
                        </Typography>
                        <Grid container spacing={3}>
                            {groups.addressInfo.map((item, index) => (
                                item.value && <React.Fragment key={`address-${index}`}>
                                    {renderDetailItem(item.label, item.value, item.icon)}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Other Details (if any) */}
                {groups.otherDetails.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            gutterBottom
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 2.5,
                                fontFamily: "'Poppins', sans-serif",
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -8,
                                    left: 0,
                                    width: 50,
                                    height: 3,
                                    background: theme.palette.primary.main,
                                    borderRadius: '8px'
                                }
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main
                                }}
                            >
                                <InfoIcon fontSize="small" />
                            </Avatar>
                            Additional Information
                        </Typography>
                        <Grid container spacing={3}>
                            {groups.otherDetails.map((item, index) => (
                                <React.Fragment key={`other-${index}`}>
                                    {renderDetailItem(item.label, item.value, item.icon)}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions 
                sx={{ 
                    px: 4, 
                    py: 3,
                    background: alpha(theme.palette.primary.main, 0.03),
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    position: 'relative',
                    gap: 2,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '10%',
                        right: '10%',
                        height: '1px',
                        background: `linear-gradient(90deg, 
                            transparent 0%, 
                            ${alpha(theme.palette.primary.main, 0.3)} 50%, 
                            transparent 100%)`
                    }
                }}
            >
                <Button 
                    onClick={onClose} 
                    variant="outlined"
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        px: 3,
                        py: 1.25,
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        borderColor: alpha(theme.palette.divider, 0.5),
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ElicalLeadDetailsModal;